/**
 * Final TDS catalog data loader.
 * Primary source: OBF Material List API (paginated).
 * Fallback source: backend polymer TDS API.
 */

import apiService from '../services/api';
import {
  buildMfiLookupKey,
  extractMfiValue,
  getTdsRecordKey,
  normalizeTdsRecord,
  resolveMfiForRow,
  resolveTdsPdfUrl,
  partitionTdsByPdf,
  sortTdsCatalogRecords,
} from './tdsData';

const OBF_MATERIAL_API = 'https://bsbprime.bsbinfotrade.com/api/obfMaterialList';
const OFFER_LIST_API = 'https://bsbprime.bsbinfotrade.com/api/offerListOpenApi';
const DEFAULT_PAGE_SIZE = 10;
const CATALOG_FETCH_PAGE_SIZE = 100;
const OBF_BEARER_TOKEN =
  process.env.REACT_APP_OBF_BEARER_TOKEN ||
  '4318731|mr2qLX1Ljgz3qSSj8939BDx5BelhhtC9POuj720z';

function dedupeRecords(list) {
  const seen = new Set();
  const out = [];
  list.forEach((item) => {
    if (!item.codeName) return;
    const key = getTdsRecordKey(item);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });
  return sortTdsCatalogRecords(out);
}

/** Full catalog cache per search — PDF rows first globally, then no-PDF rows */
const fullCatalogCache = new Map();

async function fetchAllObfRecords(searchText = '') {
  const cacheKey = searchText || '';
  if (fullCatalogCache.has(cacheKey)) {
    return fullCatalogCache.get(cacheKey);
  }

  const loadPromise = (async () => {
    const mfiLookupMap = await fetchMfiLookupMap();
    const allRows = [];
    let pageNo = 1;
    let totalPages = 1;

    while (pageNo <= totalPages) {
      const result = await fetchObfPage(pageNo, CATALOG_FETCH_PAGE_SIZE, searchText);
      if (!result.ok) {
        if (pageNo === 1) return [];
        break;
      }
      allRows.push(...result.rows);
      const items = result.totalItems ?? allRows.length;
      totalPages =
        result.totalPages || Math.max(1, Math.ceil(items / CATALOG_FETCH_PAGE_SIZE));
      pageNo += 1;
      if (pageNo <= totalPages) await sleep(120);
    }

    const deduped = dedupeRecords(allRows.map((row) => normalizeObfRow(row, mfiLookupMap)));
    const { withPdf, withoutPdf } = partitionTdsByPdf(deduped);
    return {
      withPdf,
      withoutPdf,
      ordered: [...withPdf, ...withoutPdf],
    };
  })();

  fullCatalogCache.set(cacheKey, loadPromise);
  try {
    return await loadPromise;
  } catch (err) {
    fullCatalogCache.delete(cacheKey);
    throw err;
  }
}

let mfiLookupCache = null;
let mfiLookupPromise = null;

async function fetchMfiLookupMap() {
  if (mfiLookupCache) return mfiLookupCache;
  if (mfiLookupPromise) return mfiLookupPromise;

  mfiLookupPromise = (async () => {
    const map = new Map();
    try {
      const response = await fetch(OFFER_LIST_API, {
        method: 'POST',
        mode: 'cors',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${OBF_BEARER_TOKEN}`,
        },
        credentials: 'omit',
        body: JSON.stringify({}),
      });

      if (!response.ok) return map;

      const payload = await response.json();
      const offers = extractObfRows(payload);

      offers.forEach((offer) => {
        const mfi = extractMfiValue(offer);
        if (!mfi) return;

        const codeName = offer.code_name || offer.grade_code_name || offer.grade_code || '';
        const keys = [
          buildMfiLookupKey({
            codeName,
            gradeName: offer.grade_name,
            processName: offer.process_name,
            brandName: offer.brand_name,
          }),
          buildMfiLookupKey({ codeName }),
        ].filter(Boolean);

        keys.forEach((key) => {
          if (!map.has(key)) map.set(key, mfi);
        });
      });
    } catch (err) {
      console.warn('TDS MFI lookup from offers API failed:', err);
    }

    mfiLookupCache = map;
    return map;
  })();

  return mfiLookupPromise;
}

const normalizeObfRow = (row = {}, mfiLookupMap = null) => {
  const codeName = row.grade_code_name || row.grade_code || row.code_name || row.code || '';
  const gradeName = row.grade_name || row.grade || row.polymer_grade || '';
  const processName = row.process_name || row.process || row.process_type || '';
  const brandName = row.brand_name || row.brand || row.manufacturer || '';
  const resolvedMfi = resolveMfiForRow(row, mfiLookupMap);
  const mfiValue = resolvedMfi || 'N/A';

  const rawPdf = row.tds_url || row.tds_pdf || row.tds_pdf_url || row.file_url || '';
  const normalizedPdf = resolveTdsPdfUrl(rawPdf);

  return normalizeTdsRecord({
    ...row,
    code_name: codeName,
    grade_name: gradeName,
    process_name: processName,
    brand_name: brandName,
    mfi_value: mfiValue,
    tds_url: rawPdf,
    tds_pdf: normalizedPdf,
    content:
      row.content ||
      `${brandName || 'Polymer'} ${codeName || ''} ${gradeName || ''} ${processName || ''} technical data sheet details from BSB Fortrade catalog.`,
  });
};

const extractObfRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  if (Array.isArray(payload.result)) return payload.result;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
};

const extractTotalPages = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const candidates = [
    payload.totalPages,
    payload.total_pages,
    payload.last_page,
    payload.pages,
    payload.page_count,
    payload.data?.totalPages,
    payload.data?.total_pages,
    payload.data?.last_page,
    payload.data?.pages,
    payload.pagination?.totalPages,
    payload.pagination?.total_pages,
    payload.pagination?.last_page,
  ];
  for (const value of candidates) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const extractTotalItems = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const candidates = [
    payload.totalCount,
    payload.total_count,
    payload.total,
    payload.total_items,
    payload.records_total,
    payload.data?.totalCount,
    payload.data?.total_count,
    payload.data?.total,
    payload.data?.total_items,
    payload.pagination?.totalCount,
    payload.pagination?.total,
    payload.pagination?.total_items,
    payload.pagination?.total_count,
  ];
  for (const value of candidates) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }
  return null;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchObfPage(pageNo, pageSize, searchText = '', attempt = 0) {
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${OBF_BEARER_TOKEN}`,
  };
  const requestBody = {
    search_text: searchText,
    page_number: pageNo,
    page_size: pageSize,
  };

  const response = await fetch(OBF_MATERIAL_API, { 
    method: 'POST',
    mode: 'cors',
    headers,
    credentials: 'omit',
    body: JSON.stringify(requestBody),
  });

  if (response.status === 429 && attempt < 3) {
    const retryAfter = Number(response.headers.get('Retry-After'));
    const waitMs = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : 1200 * (attempt + 1);
    await sleep(waitMs);
    return fetchObfPage(pageNo, pageSize, searchText, attempt + 1);
  }

  if (!response.ok) {
    return { ok: false, rows: [], totalPages: null, error: `HTTP ${response.status}` };
  }

  let responseData = null;
  try {
    responseData = await response.json();
  } catch (error) {
    return { ok: false, rows: [], totalPages: null, error: 'Invalid JSON response' };
  }

  return {
    ok: true,
    rows: extractObfRows(responseData),
    totalPages: extractTotalPages(responseData),
    totalItems: extractTotalItems(responseData),
    error: null,
  };
}

async function fetchObfCatalog(pageNo = 1, pageSize = DEFAULT_PAGE_SIZE, searchText = '') {
  const [first, mfiLookupMap] = await Promise.all([
    fetchObfPage(pageNo, pageSize, searchText),
    fetchMfiLookupMap(),
  ]);

  if (!first.ok) {
    return {
      success: false,
      records: [],
      notice:
        first.error === 'HTTP 429'
          ? 'Rate limit reached (429). Please wait a few seconds and try again.'
          : `OBF API unavailable (${first.error}).`,
      pageNumber: pageNo,
      totalPages: null,
      totalItems: null,
    };
  }

  const records = dedupeRecords(first.rows.map((row) => normalizeObfRow(row, mfiLookupMap)));
  return {
    success: records.length > 0,
    records,
    notice: records.length ? null : 'OBF API returned no rows.',
    pageNumber: pageNo,
    totalPages: first.totalPages || Math.ceil((first.totalItems || records.length) / pageSize) || 1,
    totalItems: Number.isFinite(first.totalItems) ? first.totalItems : records.length,
  };
}

/** Distinct filter values from a larger API sample (page 1, size 100) */
export async function fetchTdsFilterMeta() {
  const sample = await fetchObfPage(1, 100, '');
  if (!sample.ok || !sample.rows.length) {
    return { families: [], processes: [], brands: [] };
  }
  const mfiLookupMap = await fetchMfiLookupMap();
  const records = sample.rows.map((row) => normalizeObfRow(row, mfiLookupMap));
  return {
    families: [...new Set(records.map((r) => r.polymerFamily).filter(Boolean))].sort(),
    processes: [...new Set(records.map((r) => r.processName).filter(Boolean))].sort(),
    brands: [...new Set(records.map((r) => r.brandName).filter(Boolean))].sort(),
  };
}

export async function fetchTdsCatalog(options = {}) {
  const pageNumber = Number(options.pageNumber) > 0 ? Number(options.pageNumber) : 1;
  const requestedSize = Number(options.pageSize);
  const pageSize = [10, 25, 50, 100].includes(requestedSize) ? requestedSize : DEFAULT_PAGE_SIZE;
  const searchText = String(options.searchText || '').trim();

  try {
    const catalog = await fetchAllObfRecords(searchText);
    if (catalog.ordered.length > 0) {
      const totalItems = catalog.ordered.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
      const safePage = Math.min(pageNumber, totalPages);
      const start = (safePage - 1) * pageSize;
      const pageRecords = catalog.ordered.slice(start, start + pageSize);

      return {
        records: pageRecords,
        source: 'obf-material-list',
        notice: null,
        pageNumber: safePage,
        totalPages,
        totalItems,
        pageSize,
        pdfCount: catalog.withPdf.length,
        noPdfCount: catalog.withoutPdf.length,
      };
    }
  } catch (err) {
    console.error('TDS full catalog load error:', err);
  }

  const obfCatalog = await fetchObfCatalog(pageNumber, pageSize, searchText);
  if (obfCatalog.success) {
    return {
      records: obfCatalog.records,
      source: 'obf-material-list',
      notice: null,
      pageNumber: obfCatalog.pageNumber,
      totalPages: obfCatalog.totalPages,
      totalItems: obfCatalog.totalItems,
      pageSize,
    };
  }

  const apiResponse = await apiService.getPolymerTdsList();
  if (apiResponse?.success && Array.isArray(apiResponse.data) && apiResponse.data.length > 0) {
    const records = dedupeRecords(apiResponse.data.map((row) => normalizeTdsRecord(row)));
    return {
      records,
      source: 'backup-polymer-tds-api',
      notice: 'Using backup TDS API because OBF API is unavailable.',
      pageNumber: 1,
      totalPages: 1,
      totalItems: records.length,
      pageSize,
    };
  }

  return {
    records: [],
    source: 'unavailable',
    notice: obfCatalog.notice || 'TDS catalog API is currently unavailable.',
    pageNumber,
    totalPages: null,
    totalItems: null,
    pageSize,
  };
}

export async function fetchTdsRecordBySlug(slugParam) {
  const q = decodeURIComponent(String(slugParam || '')).trim();
  if (!q) return null;

  const target = q.toLowerCase();
  const codeHint = q.replace(/-tds-pdf$/i, '').split('-').filter(Boolean).pop() || q;
  const searches = [codeHint, q.replace(/-tds-pdf$/i, '').replace(/-/g, ' '), q];

  const mfiLookupMap = await fetchMfiLookupMap();

  for (const searchTerm of [...new Set(searches.filter(Boolean))]) {
    const result = await fetchObfPage(1, 25, searchTerm);
    if (!result.ok || !result.rows.length) continue;

    const normalized = dedupeRecords(result.rows.map((row) => normalizeObfRow(row, mfiLookupMap)));
    const match =
      normalized.find((r) => r.slug === target) ||
      normalized.find((r) => r.codeName.toLowerCase() === codeHint.toLowerCase()) ||
      normalized.find((r) => target.includes(r.codeName.toLowerCase())) ||
      null;

    if (match) return match;
  }

  return null;
}

